// BS needs to be imported before React and other imports:
// https://docs.bugsnag.com/platforms/browsers/react/#bundled
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import React from 'react';

import TptConnectError from 'dpl/shared/fetching/utils/TptConnectError';
import { pick } from 'dpl/shared/utils';

// eslint-disable-next-line
let BugsnagErrorBoundary = ({ children }) => children;

// HACK: BS degrades performance signficantly on /explore pages. not sure for
// cause but could be related to the page's heavy mem consumption, or frequent
// pushes to history state.
const isExplorePage = window.location.pathname.startsWith('/explore');

function frameHostname(frame) {
  try {
    return new URL(frame.file).hostname;
  } catch (err) {
    return null;
  }
}

export function isThirdPartyScript(event) {
  try {
    const hostname = frameHostname(event.errors[0].stacktrace[0]);
    return (
      hostname !== null &&
      ![
        window.__DPL_HOST, // inline scripts
        window.__DPL_ASSET_HOST // scripts from our CDN
      ].includes(hostname)
    );
  } catch (err) {
    return false;
  }
}

// Non-OK responses from our own server (mostly 422 validation failures, plus
// 401/428/429) are outcomes the server chose, not client bugs: the client
// handles them with toasts and form errors, and the server reports 5xx
// itself. The fetch layer raises a TptConnectError carrying the response for
// each one. ~32k/month of these reached Bugsnag through unhandled
// `mutateAsync` rejections and catch-all `notify(error)` calls.
function isServerResponse(event) {
  const { originalError } = event;

  return (
    originalError instanceof TptConnectError && Boolean(originalError.response)
  );
}

// First onError callback. Bugsnag drops the event, without sending it, when
// a callback returns false.
export function shouldSendEvent(event) {
  return !isServerResponse(event);
}

// Bugsnag groups errors by the reported function name of the "significant"
// stack frame, but Vite/esbuild minification rotates those names on every
// build (`DB.render` one deploy, `SB.render` the next) and browsers word the
// same failure differently, so a single bug fragments into many low-count
// error groups. When we can extract a build- and browser-stable signature
// from the error, we group on it instead. Everything else (minified
// line/column/method, raw message text, content-hashed filenames) changes
// per deploy or per browser and must stay out of the hash.

// Only frames from our CDN bundles have deploy-stable paths; inline-script
// frames carry the page URL, which varies per route.
function isOwnBundleFrame(frame) {
  return frameHostname(frame) === window.__DPL_ASSET_HOST;
}

// `main-B43LycMX.js` (Vite) or `main.8f3a1c2d.js` (webpack) -> `main.js`
function normalizeFile(file) {
  return new URL(file).pathname.replace(/[-.][\w-]{8,}(\.\w+)$/, '$1');
}

// Extracts the trailing property name from a member expression like `Pe.id`
// or `window.webkit.messageHandlers[t].postMessage`. Leading identifiers are
// minified (they rotate every build) but property names are not, so only a
// property reached via a `.` is stable enough to group on.
function stablePropertyName(expression) {
  const segments = expression
    .replace(/^new\s+/, '')
    .replace(/\([^(]*\)$/, '')
    .split('.');

  if (segments.length < 2) {
    return null;
  }

  const property = segments.pop().replace(/\[[^\]]*\]$/, '');
  return /^[A-Za-z_$][\w$]*$/.test(property) ? property : null;
}

function propertyReadSignature(value, property) {
  return property && `${value}.${property}`;
}

function notCallableSignature(expression, kind) {
  const property = stablePropertyName(expression);
  return property && `${property} is not a ${kind}`;
}

const MESSAGE_MATCHERS = [
  // Chrome: Cannot read properties of undefined (reading 'id')
  {
    pattern:
      /^Cannot read propert(?:ies|y) of (undefined|null) \(reading '([^']+)'\)/,
    signature: ([, value, property]) => propertyReadSignature(value, property)
  },
  // Chrome <93 / Edge legacy: Cannot read property 'id' of undefined
  {
    pattern: /^Cannot read property '([^']+)' of (undefined|null)/,
    signature: ([, property, value]) => propertyReadSignature(value, property)
  },
  // Firefox: can't access property "id", Pe is undefined
  {
    pattern: /^can't access property "([^"]+)", .+ is (undefined|null)/,
    signature: ([, property, value]) => propertyReadSignature(value, property)
  },
  // Safari: undefined is not an object (evaluating 'Pe.id')
  {
    pattern: /^(undefined|null) is not an object \(evaluating '([^']+)'\)/,
    signature: ([, value, expression]) =>
      propertyReadSignature(value, stablePropertyName(expression))
  },
  // Chrome/Firefox: Pe.push is not a function / X is not a constructor.
  // Also matches Safari's longer form:
  //   t.filter is not a function. (In 't.filter(...)', 't.filter' is undefined)
  {
    pattern: /^(\S+) is not a (function|constructor)/,
    signature: ([, expression, kind]) => notCallableSignature(expression, kind)
  },
  // Safari: undefined is not a constructor (evaluating 'new window.google.maps.LatLng(e,o)')
  {
    pattern:
      /^(?:undefined|null) is not a (function|constructor) \(evaluating '([^']+)'\)/,
    signature: ([, kind, expression]) => notCallableSignature(expression, kind)
  }
];

function canonicalizeMessage(message) {
  for (let i = 0; i < MESSAGE_MATCHERS.length; i += 1) {
    const { pattern, signature } = MESSAGE_MATCHERS[i];
    const match = message.match(pattern);
    const result = match && signature(match);
    if (result) {
      return result;
    }
  }
  return null;
}

export function computeGroupingHash(event) {
  try {
    const { errorClass, errorMessage, stacktrace } = event.errors[0];

    const signature = canonicalizeMessage(errorMessage);
    if (!signature) {
      return undefined;
    }

    const frame = stacktrace.find(isOwnBundleFrame);
    if (!frame) {
      return undefined;
    }

    return `${errorClass} ${signature} in ${normalizeFile(frame.file)}`;
  } catch (err) {
    return undefined;
  }
}

export function lowerSeverityIfNeeded(event) {
  if (event.severity !== 'error') {
    return;
  }

  if (window.__DPL_UNSUPPORTED_BROWSER) {
    event.severity = 'warning';
    event.addMetadata('severityReason', 'Unsupported browser');
    return;
  }

  if (window.__DPL_HOST !== window.location.hostname) {
    event.severity = 'warning';
    event.addMetadata('severityReason', 'Proxied request');
    return;
  }

  if (isThirdPartyScript(event)) {
    event.severity = 'warning';
    event.addMetadata('severityReason', 'Third-party script');
  }
}

if (CONFIG.bugsnagKey && !isExplorePage) {
  if (!window.bugsnagClient) {
    window.bugsnagClient = Bugsnag.start({
      releaseStage: CONFIG.bugsnagReleaseStage,
      apiKey: CONFIG.bugsnagKey,
      appVersion: CONFIG.appVersion,
      plugins: [new BugsnagPluginReact(React)],
      user: {
        id: window.__DPL_CURRENT_USER_ID
      },
      context: window.__DPL_PAGE_INFO?.category,
      onError: [
        shouldSendEvent,
        event => {
          lowerSeverityIfNeeded(event);
          event.addMetadata('debugSessionId', window.__DPL_DEBUG_SESSION_ID);
          event.addMetadata(
            'network',
            'connection',
            pick(window.navigator.connection || {}, [
              'downlink',
              'effectiveType'
            ])
          );

          // Bugsnag doesn't surface groupingHash in its UI, so mirror it into
          // metadata to make the custom grouping auditable per event.
          const groupingHash = computeGroupingHash(event);
          if (groupingHash) {
            event.groupingHash = groupingHash;
            event.addMetadata('grouping', 'hash', groupingHash);
          }
        }
      ]
    });
  }

  window.addEventListener('offline', () => {
    Bugsnag.addMetadata('network', 'wentOffline', new Date().toISOString());
  });

  window.addEventListener('online', () => {
    Bugsnag.addMetadata('network', 'wentOnline', new Date().toISOString());
  });

  BugsnagErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React);
}

export default BugsnagErrorBoundary;
